import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import GenericForm from '../../components/GenericForm/GenericForm';
import Spinner from '../../components/Spinner/Spinner';
import './Login.module.css'; //just for buttons width: 100% if changes to hold classes change the way it is imported
import { login as apiLogin, forgotPassword, resendConfirmationCode, confirmResetRequest, getSystemTables } from '../../services/ApiService';
import { useAuth } from '../../context/AuthContext';
import { defaultErrorHandler } from '../../services/ErrorHandler';
import { ErrorType } from '../../models/ApiError';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    userName: '',
    password: '',
    newPassword: '',
    confirmationCode: '', // Added for forgot password functionality
  });

  const [errors, setErrors] = useState<{
    userName: string;
    password: string;
    newPassword: string;
    confirmationCode: string;
  }>({
    userName: '',
    password: '',
    newPassword: '',
    confirmationCode: '',
  });

  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const validatePassword = (password: string): string => {
    const rules = [
      { regex: /[A-Z]/, message: t('Login.Errors.Password.Uppercase') },
      { regex: /[a-z]/, message: t('Login.Errors.Password.Lowercase') },
      { regex: /\d/, message: t('Login.Errors.Password.Number') },
      { regex: /[\W_]/, message: t('Login.Errors.Password.Symbol') },
      { regex: /.{8,}/, message: t('Login.Errors.Password.Length') },
    ];

    for (const rule of rules) {
      if (!rule.regex.test(password)) return rule.message;
    }

    return '';
  };

  const loginMutation = useMutation({
    mutationFn: async () => {
      return await apiLogin(formData.userName, formData.password, formData.newPassword);
    },
    onSuccess: async (data) => {
      if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setIsNewPasswordRequired(true);
      } else {
        auth.login({
          idToken: data.tokens.idToken,
          refreshToken: data.tokens.refreshToken,
        }, formData.userName);

        // Optionally prefetch system tables:
        try {
          // This triggers getSystemTables() and caches under ['systemTables'] key
          await queryClient.prefetchQuery({
            queryKey: ['systemTables'],
            queryFn: getSystemTables,
          });
        } catch (error) {
          console.error('Error prefetching system tables:', error);
        }
        navigate('/order-history');
      }
    },
    onError: (error: any) => {
      const apiError = defaultErrorHandler.handle(error);
      const userMessage = defaultErrorHandler.getUserFriendlyMessage(apiError);
      
      setErrors((prev) => ({
        ...prev,
        password: userMessage,
      }));

      // Handle specific error types
      if (apiError.type === ErrorType.AUTHENTICATION_ERROR) {
        const message = apiError.message.toLowerCase();
        if (message.includes('password reset required')) {
          setIsForgotPassword(true);
        }
      }
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async () => {
      return await forgotPassword(formData.userName);
    },
    onSuccess: () => {
      setIsForgotPassword(true);
      alert(t('Login.Messages.ConfirmationCodeSent'));
    },
    onError: (error: any) => {
      const apiError = defaultErrorHandler.handle(error);
      const userMessage = defaultErrorHandler.getUserFriendlyMessage(apiError);
      
      setErrors((prev) => ({
        ...prev,
        userName: userMessage,
      }));
    },
  });

  const resendCodeMutation = useMutation({
    mutationFn: async () => {
      return await resendConfirmationCode(formData.userName);
    },
    onSuccess: () => {
      console.log('Confirmation code resent successfully.');
      alert(t('Login.Messages.ConfirmationCodeSent'));
    },
    onError: (error: any) => {
      const apiError = defaultErrorHandler.handle(error);
      const userMessage = defaultErrorHandler.getUserFriendlyMessage(apiError);
      
      setErrors((prev) => ({
        ...prev,
        userName: userMessage,
      }));
    },
  });

  const confirmResetMutation = useMutation({
    mutationFn: async () => {
      return await confirmResetRequest(formData.userName, formData.confirmationCode, formData.newPassword);
    },
    onSuccess: () => {
      alert(t('Login.ResetSuccess')); // Notify success
      setIsForgotPassword(false);
      setFormData({
        userName: '',
        password: '',
        newPassword: '',
        confirmationCode: '',
      });
    },
    onError: (error: any) => {
      const apiError = defaultErrorHandler.handle(error);
      const userMessage = defaultErrorHandler.getUserFriendlyMessage(apiError);
      
      setErrors((prev) => ({
        ...prev,
        confirmationCode: userMessage,
      }));
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      userName: '',
      password: '',
      newPassword: '',
      confirmationCode: '',
    };

    if (!formData.userName) {
      newErrors.userName = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.UserName') });
    }
    if (isNewPasswordRequired || isForgotPassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.NewPassword') });
      } else {
        const passwordError = validatePassword(formData.newPassword);
        if (passwordError) newErrors.newPassword = passwordError;
      }
    }
    if (isForgotPassword && !formData.confirmationCode) {
      newErrors.confirmationCode = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.ConfirmationCode') });
    }
    if (!isForgotPassword && !formData.password) {
      newErrors.password = t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.Password') });
    }

    if (Object.values(newErrors).some((err) => err)) {
      setErrors(newErrors);
      return;
    }

    if (isForgotPassword) {
      console.log('Forgot password submission:', formData);
      confirmResetMutation.mutate();
    } else {
      loginMutation.mutate();
    }
  };

  const handleForgotPassword = () => {
    if (!formData.userName) {
      setErrors((prev) => ({
        ...prev,
        userName: t('Login.Errors.EmptyInput', { InputLabel: t('Login.Labels.UserName') }),
      }));
      return;
    }
    forgotPasswordMutation.mutate();
  };

  const handleResendCode = () => {
    resendCodeMutation.mutate();
  };

  const handleBack = () => {
    setIsForgotPassword(false);
    setErrors({
      userName: '',
      password: '',
      newPassword: '',
      confirmationCode: '',
    });
  };

  const inputs = isForgotPassword
    ? [
        {
          name: 'userName',
          label: t('Login.Labels.UserName'),
          type: 'text',
          value: formData.userName,
          error: errors.userName,
        },
        {
          name: 'confirmationCode',
          label: t('Login.Labels.ConfirmationCode'),
          type: 'text',
          value: formData.confirmationCode,
          error: errors.confirmationCode,
        },
        {
          name: 'newPassword',
          label: t('Login.Labels.NewPassword'),
          type: 'password',
          value: formData.newPassword,
          error: errors.newPassword,
        },
      ]
    : [
        {
          name: 'userName',
          label: t('Login.Labels.UserName'),
          type: 'text',
          value: formData.userName,
          error: errors.userName,
        },
        {
          name: 'password',
          label: t('Login.Labels.Password'),
          type: 'password',
          value: formData.password,
          error: errors.password,
        },
        ...(isNewPasswordRequired
          ? [
              {
                name: 'newPassword',
                label: t('Login.Labels.NewPassword'),
                type: 'password',
                value: formData.newPassword,
                error: errors.newPassword,
              },
            ]
          : []),
      ];

  return (
    <div style={{ margin: '2rem auto', maxWidth: '400px' }}>
      {(loginMutation.isPending || forgotPasswordMutation.isPending || resendCodeMutation.isPending || confirmResetMutation.isPending) && <Spinner />}
      <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        {isForgotPassword
          ? t('Login.Titles.TitleForgotPassword')
          : isNewPasswordRequired
          ? t('Login.Titles.TitleNewPassword')
          : t('Login.Titles.Title')}
      </h2>
      <GenericForm
        inputs={inputs}
        onChange={handleInputChange}
        onSubmit={handleFormSubmit}
        submitLabel={(isForgotPassword || isNewPasswordRequired) ? t('General.Labels.Confirm') : t('Login.Titles.Title')}
      />
      {isForgotPassword ? (
        <>
          <button onClick={handleResendCode}>
            {t('Login.Labels.ResendConfirmationCode')}
          </button>
          <button onClick={handleBack}>
            {t('General.Labels.Back')}
          </button>
        </>
      ) : (
        <button onClick={handleForgotPassword}>
          {t('Login.Labels.ForgotPassword')}
        </button>
      )}
    </div>
  );
};

export default Login;